import type { FC, ReactNode } from 'react'
import type { Patient } from '@/lib/clinic/types'
import type { ConfirmOptions } from '@/components/common/confirm-dialog'
import type { PatientSaveIntent } from '@/components/patients/patient-form-dialog'
import type { PickerMode } from '@/components/patients/patient-search-dialog'

import { useRouter } from 'next/router'
import { toast } from 'sonner'
import { useClinic } from '@/hooks/use-clinic'
import { formatDate, formatDateTime } from '@/lib/clinic/datetime'
import { pluralize, telHref } from '@/lib/clinic/format'
import ConfirmDialog from '@/components/common/confirm-dialog'
import FollowUpDialog from '@/components/follow-ups/follow-up-dialog'
import RescheduleDialog from '@/components/follow-ups/reschedule-dialog'
import WhatsAppDialog from '@/components/follow-ups/whatsapp-dialog'
import PatientFormDialog from '@/components/patients/patient-form-dialog'
import PatientSearchDialog from '@/components/patients/patient-search-dialog'
import PrintPortal from '@/components/prescriptions/print-portal'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'

interface RegisterOptions {
    name?: string
    phone?: string
}

interface FollowUpOptions {
    patientId: string
    consultationId?: string | null
    followUpId?: string | null
}

interface ClinicUiValue {
    registerPatient: (options?: RegisterOptions) => void
    editPatient: (patientId: string) => void
    openPatientPicker: (mode?: PickerMode) => void
    startConsultation: (patientCode?: string) => void
    openWhatsApp: (patientId: string, followUpId?: string | null) => void
    callPatient: (patientId: string, followUpId?: string | null) => void
    openFollowUp: (options: FollowUpOptions) => void
    openReschedule: (followUpId: string) => void
    completeFollowUp: (followUpId: string) => void
    reopenFollowUp: (followUpId: string) => void
    confirm: (options: ConfirmOptions) => Promise<boolean>
    deletePatient: (patientId: string) => Promise<boolean>
    deleteConsultation: (consultationId: string) => Promise<boolean>
    deletePrescription: (prescriptionId: string) => Promise<boolean>
    deleteFollowUp: (followUpId: string) => Promise<boolean>
    printPrescription: (prescriptionId: string) => void
    downloadPrescriptionPdf: (prescriptionId: string) => Promise<void>
    pinPrintable: (prescriptionId: string | null) => void
}

interface ClinicUiProviderProps {
    children: ReactNode
}

interface PatientDialogState {
    open: boolean
    session: number
    patientId: string | null
    name: string
    phone: string
}

interface PickerState {
    open: boolean
    session: number
    mode: PickerMode
}

interface ContactState {
    open: boolean
    session: number
    patientId: string | null
    followUpId: string | null
}

interface FollowUpDialogState {
    open: boolean
    session: number
    patientId: string | null
    consultationId: string | null
    followUpId: string | null
}

interface RescheduleState {
    open: boolean
    session: number
    followUpId: string | null
}

interface ConfirmState extends ConfirmOptions {
    open: boolean
}

interface PrintJob {
    id: string
    nonce: number
}

const ClinicUiContext = createContext<ClinicUiValue | null>(null)

const looksLikePhone = (value: string): boolean => {
    const compact = value.replace(/[\s()+-]/g, '')

    return compact.length >= 5 && /^\d+$/.test(compact)
}

export const ClinicUiProvider: FC<ClinicUiProviderProps> = ({ children }): ReactNode => {
    const router = useRouter()
    const clinic = useClinic()
    const latest = useRef(clinic)

    latest.current = clinic

    const [patientDialog, setPatientDialog] = useState<PatientDialogState>({ open: false, session: 0, patientId: null, name: '', phone: '' })
    const [picker, setPicker] = useState<PickerState>({ open: false, session: 0, mode: 'open' })
    const [whatsApp, setWhatsApp] = useState<ContactState>({ open: false, session: 0, patientId: null, followUpId: null })
    const [followUpDialog, setFollowUpDialog] = useState<FollowUpDialogState>({ open: false, session: 0, patientId: null, consultationId: null, followUpId: null })
    const [reschedule, setReschedule] = useState<RescheduleState>({ open: false, session: 0, followUpId: null })
    const [confirmState, setConfirmState] = useState<ConfirmState>({ open: false, title: '' })
    const [printJob, setPrintJob] = useState<PrintJob | null>(null)
    const [pinnedPrintable, setPinnedPrintable] = useState<string | null>(null)
    const confirmResolver = useRef<((value: boolean) => void) | null>(null)

    const navigate = useCallback((href: string) => {
        void router.push(href)
    }, [router])

    const registerPatient = useCallback((options: RegisterOptions = {}) => {
        setPicker(current => ({ ...current, open: false }))
        setPatientDialog(current => ({
            open: true,
            session: current.session + 1,
            patientId: null,
            name: options.name ?? '',
            phone: options.phone ?? ''
        }))
    }, [])

    const editPatient = useCallback((patientId: string) => {
        setPatientDialog(current => ({ open: true, session: current.session + 1, patientId, name: '', phone: '' }))
    }, [])

    const openPatientPicker = useCallback((mode: PickerMode = 'open') => {
        setPicker(current => ({ open: true, session: current.session + 1, mode }))
    }, [])

    const startConsultation = useCallback((patientCode?: string) => {
        if (patientCode) {
            navigate(`/consultations/new?patient=${encodeURIComponent(patientCode)}`)
            return
        }

        openPatientPicker('consultation')
    }, [navigate, openPatientPicker])

    const openWhatsApp = useCallback((patientId: string, followUpId: string | null = null) => {
        setWhatsApp(current => ({ open: true, session: current.session + 1, patientId, followUpId }))
    }, [])

    const callPatient = useCallback((patientId: string, followUpId: string | null = null) => {
        const patient = latest.current.index.patientsById.get(patientId)
        if (!patient) return

        latest.current.actions.logReminder({ patientId, followUpId, channel: 'call', message: '' })
        window.location.href = telHref(patient.phone)
        toast.success(`Calling ${patient.name}`, { description: 'The call is saved in the reminder history.' })
    }, [])

    const openFollowUp = useCallback(({ patientId, consultationId = null, followUpId = null }: FollowUpOptions) => {
        setFollowUpDialog(current => ({ open: true, session: current.session + 1, patientId, consultationId, followUpId }))
    }, [])

    const openReschedule = useCallback((followUpId: string) => {
        setReschedule(current => ({ open: true, session: current.session + 1, followUpId }))
    }, [])

    const completeFollowUp = useCallback((followUpId: string) => {
        const { actions, index } = latest.current
        const updated = actions.completeFollowUp(followUpId)
        if (!updated) return

        const patient = index.patientsById.get(updated.patientId)

        toast.success('Follow-up marked as completed', {
            description: patient?.name,
            action: { label: 'Undo', onClick: () => latest.current.actions.reopenFollowUp(followUpId) }
        })
    }, [])

    const reopenFollowUp = useCallback((followUpId: string) => {
        const updated = latest.current.actions.reopenFollowUp(followUpId)
        if (updated) toast.success('Follow-up moved back to pending')
    }, [])

    const confirm = useCallback((options: ConfirmOptions) => {
        return new Promise<boolean>(resolve => {
            confirmResolver.current?.(false)
            confirmResolver.current = resolve
            setConfirmState({ ...options, open: true })
        })
    }, [])

    const handleConfirmResult = useCallback((result: boolean) => {
        confirmResolver.current?.(result)
        confirmResolver.current = null
        setConfirmState(current => ({ ...current, open: false }))
    }, [])

    const deletePatient = useCallback(async (patientId: string) => {
        const { index } = latest.current
        const patient = index.patientsById.get(patientId)
        if (!patient) return false

        const consultations = index.consultationsByPatient.get(patientId)?.length ?? 0
        const prescriptions = index.prescriptionsByPatient.get(patientId)?.length ?? 0
        const followUps = index.followUpsByPatient.get(patientId)?.length ?? 0

        const confirmed = await confirm({
            title: `Delete ${patient.name}?`,
            description: `This permanently removes ${patient.code} together with ${pluralize(consultations, 'consultation')}, ${pluralize(prescriptions, 'prescription')} and ${pluralize(followUps, 'follow-up')}. This cannot be undone.`,
            confirmLabel: 'Delete patient',
            destructive: true
        })

        if (!confirmed) return false

        latest.current.actions.deletePatient(patientId)
        toast.success('Patient deleted', { description: `${patient.name} · ${patient.code}` })

        return true
    }, [confirm])

    const deleteConsultation = useCallback(async (consultationId: string) => {
        const { index } = latest.current
        const consultation = index.consultationsById.get(consultationId)
        if (!consultation) return false

        const prescriptions = index.prescriptionsByConsultation.get(consultationId)?.length ?? 0
        const followUps = index.followUpsByConsultation.get(consultationId)?.length ?? 0
        const linked = prescriptions + followUps > 0

        const confirmed = await confirm({
            title: 'Delete this consultation?',
            description: `The consultation on ${formatDateTime(consultation.consultedAt)} will be permanently removed.${linked ? ` Its ${pluralize(prescriptions, 'prescription')} and ${pluralize(followUps, 'follow-up')} stay on the patient's record.` : ''}`,
            confirmLabel: 'Delete consultation',
            destructive: true
        })

        if (!confirmed) return false

        latest.current.actions.deleteConsultation(consultationId)
        toast.success('Consultation deleted')

        return true
    }, [confirm])

    const deletePrescription = useCallback(async (prescriptionId: string) => {
        const { index } = latest.current
        const prescription = index.prescriptionsById.get(prescriptionId)
        if (!prescription) return false

        const patient = index.patientsById.get(prescription.patientId)

        const confirmed = await confirm({
            title: `Delete prescription ${prescription.number}?`,
            description: `The prescription from ${formatDateTime(prescription.prescribedAt)}${patient ? ` for ${patient.name}` : ''} will be permanently removed.`,
            confirmLabel: 'Delete prescription',
            destructive: true
        })

        if (!confirmed) return false

        latest.current.actions.deletePrescription(prescriptionId)
        toast.success('Prescription deleted', { description: prescription.number })

        return true
    }, [confirm])

    const deleteFollowUp = useCallback(async (followUpId: string) => {
        const { index } = latest.current
        const followUp = index.followUpsById.get(followUpId)
        if (!followUp) return false

        const patient = index.patientsById.get(followUp.patientId)

        const confirmed = await confirm({
            title: 'Delete this follow-up?',
            description: `The follow-up${patient ? ` for ${patient.name}` : ''} on ${formatDate(followUp.dueDate)} will be removed. Its reminder history stays on the patient's record.`,
            confirmLabel: 'Delete follow-up',
            destructive: true
        })

        if (!confirmed) return false

        latest.current.actions.deleteFollowUp(followUpId)
        toast.success('Follow-up deleted')

        return true
    }, [confirm])

    const printPrescription = useCallback((prescriptionId: string) => {
        setPrintJob({ id: prescriptionId, nonce: Date.now() })
    }, [])

    const pinPrintable = useCallback((prescriptionId: string | null) => {
        setPinnedPrintable(prescriptionId)
    }, [])

    const downloadPrescriptionPdf = useCallback(async (prescriptionId: string) => {
        const { index, data, today } = latest.current
        const prescription = index.prescriptionsById.get(prescriptionId)
        const patient = prescription ? index.patientsById.get(prescription.patientId) : undefined

        if (!prescription || !patient) {
            toast.error('This prescription is no longer available.')
            return
        }

        const consultation = prescription.consultationId ? index.consultationsById.get(prescription.consultationId) ?? null : null
        const toastId = toast.loading('Preparing PDF')

        try {
            const pdf = await import('@/lib/clinic/prescription-pdf')
            await pdf.downloadPrescriptionPdf({ prescription, patient, consultation, settings: data.settings }, today)
            toast.success('PDF downloaded', { id: toastId, description: `${prescription.number} · ${patient.name}` })
        }
        catch (error) {
            console.error(error)
            toast.error('The PDF could not be created. Please try again.', { id: toastId })
        }
    }, [])

    const activePrintId = printJob?.id ?? pinnedPrintable

    useEffect(() => {
        const root = document.documentElement

        if (activePrintId) root.classList.add('printing-rx')
        else root.classList.remove('printing-rx')

        return () => root.classList.remove('printing-rx')
    }, [activePrintId])

    useEffect(() => {
        if (!printJob) return

        let cancelled = false

        const finish = () => {
            window.removeEventListener('afterprint', finish)
            if (!cancelled) setPrintJob(null)
        }

        const nextFrame = () => new Promise<void>(resolve => {
            requestAnimationFrame(() => resolve())
        })

        const run = async () => {
            for (let attempt = 0; attempt < 30; attempt += 1) {
                await nextFrame()
                if (document.querySelector('.print-root .rx-sheet')) break
            }

            await nextFrame()

            try {
                await document.fonts?.ready
            }
            catch {
                // Fonts are optional for printing.
            }

            const images = Array.from(document.querySelectorAll<HTMLImageElement>('.print-root img'))
            await Promise.all(images.map(image => image.decode().catch(() => undefined)))

            if (cancelled) return

            window.addEventListener('afterprint', finish)
            window.print()
        }

        void run()

        return () => {
            cancelled = true
            window.removeEventListener('afterprint', finish)
        }
    }, [printJob])

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
                event.preventDefault()
                openPatientPicker('open')
            }
        }

        window.addEventListener('keydown', handleKeyDown)

        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [openPatientPicker])

    const handlePatientSaved = (patient: Patient, intent: PatientSaveIntent) => {
        const editing = Boolean(patientDialog.patientId)

        setPatientDialog(current => ({ ...current, open: false }))

        if (editing) {
            toast.success('Patient details updated', { description: `${patient.name} · ${patient.code}` })
            return
        }

        if (intent === 'consult') {
            toast.success('Patient registered', { description: `${patient.code} · ${patient.name}` })
            navigate(`/consultations/new?patient=${encodeURIComponent(patient.code)}`)
            return
        }

        toast.success('Patient registered', {
            description: `${patient.code} · ${patient.name}`,
            action: { label: 'Open profile', onClick: () => navigate(`/patients/${encodeURIComponent(patient.code)}`) }
        })
    }

    const handleOpenExisting = (patient: Patient, intent: 'profile' | 'consult') => {
        setPatientDialog(current => ({ ...current, open: false }))
        navigate(intent === 'consult'
            ? `/consultations/new?patient=${encodeURIComponent(patient.code)}`
            : `/patients/${encodeURIComponent(patient.code)}`)
    }

    const handlePickerSelect = (patient: Patient, mode: PickerMode) => {
        setPicker(current => ({ ...current, open: false }))

        if (mode === 'open') navigate(`/patients/${encodeURIComponent(patient.code)}`)
        else if (mode === 'consultation') navigate(`/consultations/new?patient=${encodeURIComponent(patient.code)}`)
        else if (mode === 'prescription') navigate(`/prescriptions/new?patient=${encodeURIComponent(patient.code)}`)
        else openFollowUp({ patientId: patient.id })
    }

    const handlePickerRegister = (query: string) => {
        const trimmed = query.trim()

        registerPatient(looksLikePhone(trimmed) ? { phone: trimmed } : { name: trimmed })
    }

    const value = useMemo<ClinicUiValue>(() => ({
        registerPatient,
        editPatient,
        openPatientPicker,
        startConsultation,
        openWhatsApp,
        callPatient,
        openFollowUp,
        openReschedule,
        completeFollowUp,
        reopenFollowUp,
        confirm,
        deletePatient,
        deleteConsultation,
        deletePrescription,
        deleteFollowUp,
        printPrescription,
        downloadPrescriptionPdf,
        pinPrintable
    }), [
        registerPatient,
        editPatient,
        openPatientPicker,
        startConsultation,
        openWhatsApp,
        callPatient,
        openFollowUp,
        openReschedule,
        completeFollowUp,
        reopenFollowUp,
        confirm,
        deletePatient,
        deleteConsultation,
        deletePrescription,
        deleteFollowUp,
        printPrescription,
        downloadPrescriptionPdf,
        pinPrintable
    ])

    const editingPatient = patientDialog.patientId ? clinic.index.patientsById.get(patientDialog.patientId) ?? null : null

    return (
        <ClinicUiContext.Provider value={value}>
            {children}

            <PatientFormDialog
                key={`patient-${patientDialog.session}`}
                open={patientDialog.open}
                onOpenChange={open => setPatientDialog(current => ({ ...current, open }))}
                patient={editingPatient}
                initialName={patientDialog.name}
                initialPhone={patientDialog.phone}
                onSaved={handlePatientSaved}
                onOpenExisting={handleOpenExisting}
            />

            <PatientSearchDialog
                key={`picker-${picker.session}`}
                open={picker.open}
                onOpenChange={open => setPicker(current => ({ ...current, open }))}
                mode={picker.mode}
                onSelect={handlePickerSelect}
                onRegister={handlePickerRegister}
            />

            <WhatsAppDialog
                key={`whatsapp-${whatsApp.session}`}
                open={whatsApp.open}
                onOpenChange={open => setWhatsApp(current => ({ ...current, open }))}
                patientId={whatsApp.patientId}
                followUpId={whatsApp.followUpId}
            />

            <FollowUpDialog
                key={`follow-up-${followUpDialog.session}`}
                open={followUpDialog.open}
                onOpenChange={open => setFollowUpDialog(current => ({ ...current, open }))}
                patientId={followUpDialog.patientId}
                consultationId={followUpDialog.consultationId}
                followUpId={followUpDialog.followUpId}
            />

            <RescheduleDialog
                key={`reschedule-${reschedule.session}`}
                open={reschedule.open}
                onOpenChange={open => setReschedule(current => ({ ...current, open }))}
                followUpId={reschedule.followUpId}
            />

            <ConfirmDialog
                title={confirmState.title}
                description={confirmState.description}
                confirmLabel={confirmState.confirmLabel}
                cancelLabel={confirmState.cancelLabel}
                destructive={confirmState.destructive}
                open={confirmState.open}
                onResult={handleConfirmResult}
            />

            {activePrintId && <PrintPortal prescriptionId={activePrintId} />}
        </ClinicUiContext.Provider>
    )
}

export const useClinicUi = (): ClinicUiValue => {
    const context = useContext(ClinicUiContext)
    if (!context) throw new Error('useClinicUi must be used inside ClinicUiProvider')

    return context
}
